import { AddOutlined, ContentCopy, QrCode } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { FeatureNameEnum, TemplatesApi } from '@ulysses-inc/harami_api_client'
import { saveAs } from 'file-saver'
import { Dispatch, FC, SetStateAction, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { GetTemplatesFilter } from 'src/exShared/types/types'
import { flattenNodePath } from 'src/exShared/util/place/flattenNodePath'
import { getUniqueFlattenNodes } from 'src/exShared/util/place/getUniqueFlattenNodes'
import { flattenNodes } from 'src/exShared/util/place/placeNode'
import { getFilter } from 'src/exShared/util/template/getFilter'
import { useFeature } from 'src/features/featureFlags/useFeatureFlags'
import { getFileNameFromContentDisposition } from 'src/state/ducks/download/sagas'
import interceptionsActions from 'src/state/ducks/interceptions/actions'
import {
  copyTemplates,
  getTemplates,
} from 'src/state/ducks/templateList/actions'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import {
  handleHTTPError,
  isHTTPError,
} from 'src/state/middleware/saga/handleHttpError'
import { RootState } from 'src/state/store'
import { useListFilter } from '../../../hooks/filter/useListFilter'
import TemplatesFilterDropDown from './TemplatesFilterDropDown'
import TemplatesSortDropDown from './TemplatesSortDropDown'
import { templatesSortOrderStorage } from './templatesSortOrder'

const Actions: FC<{
  selectedTemplateIdsState: [Set<number>, Dispatch<SetStateAction<Set<number>>>]
}> = ({
  selectedTemplateIdsState: [selectedTemplateIds, setSelectedTemplateIds],
}) => {
  const history = useHistory()
  const {
    templateListState: { request, filter },
    placesState: {
      places: { places },
      placeGroups: { nodes: placeGroups },
    },
  } = useSelector((state: RootState) => state)

  const placeNodes = getUniqueFlattenNodes([
    ...flattenNodes(placeGroups),
    ...places,
  ])
  const flattenedNodePath = flattenNodePath([...placeNodes, ...placeGroups], [])

  const dispatch = useDispatch()

  const createTemplatePages = () => {
    history.push('/templates/create/industry')
  }

  const { filters } = useListFilter()
  const templatesSortOrder = templatesSortOrderStorage.get() ?? undefined

  const [sortColumnName, setSortColumn] = useState<string>(
    templatesSortOrder?.sortColumn ?? '',
  )
  const [orderName, setOrder] = useState<string>(
    templatesSortOrder?.sortOrder ?? 'asc',
  )

  const initFilter = {
    templateName: filters?.templates?.filter?.templateName,
    placeNodeIds: filters?.templates?.filter?.placeNodeIds,
    placeGroupNodeIds: filters?.templates?.filter?.placeGroupNodeIds,
  }

  const setSortCondition = (filter: GetTemplatesFilter | undefined) => {
    if (filter) {
      filter.sortColumn = { $in: sortColumnName }
      filter.sortOrder = { $in: orderName }
    }
    return filter
  }

  const getFilterTemplates = (filter: GetTemplatesFilter | undefined) => {
    dispatch(getTemplates(request, setSortCondition(filter)))
  }

  const sortFilter = (sortColumn: string, sortOrder: string) => {
    return {
      templateName: filters?.templates?.filter?.templateName,
      placeNodeIds: filters?.templates?.filter?.placeNodeIds,
      placeGroupNodeIds: filters?.templates?.filter?.placeGroupNodeIds,
      sortColumn,
      sortOrder,
    }
  }

  const getSortedTemplates = (
    isClear: boolean,
    sortColumn: string,
    sortOrder: string,
  ) => {
    templatesSortOrderStorage.set({
      sortColumn,
      sortOrder,
    })
    setSortColumn(sortColumn)
    setOrder(sortOrder)
    dispatch(
      getTemplates(
        request,
        getFilter(
          placeNodes,
          flattenedNodePath,
          isClear ? initFilter : sortFilter(sortColumn, sortOrder),
        ),
      ),
    )
  }

  const { canUse: canUseTemplateQrCode } = useFeature(
    FeatureNameEnum.TEMPLATE_QR_CODE,
  )
  const [isDownloadingQrCodes, setIsDownloadingQrCodes] = useState(false)
  const { showErrorSnackbar } = useSnackbar()

  const onClickQrCodeDownloadButton = async () => {
    setIsDownloadingQrCodes(true)
    try {
      const response = await baseClient
        .getApi(TemplatesApi)
        .postTemplateQrCodesRaw({
          postTemplateQrCodesRequestBody: {
            templateIds: Array.from(selectedTemplateIds),
          },
        })
        .catch(handleHTTPError)
      saveAs(
        await response.value(),
        getFileNameFromContentDisposition(
          response.raw.headers.get('content-disposition') ?? '',
        ),
      )
    } catch (error) {
      if (isHTTPError(error)) {
        dispatch(interceptionsActions.handleHttpError(error))
      } else {
        showErrorSnackbar('QRコードのダウンロードに失敗しました')
      }
    } finally {
      setIsDownloadingQrCodes(false)
    }
  }

  const onClickCopyButton = () => {
    dispatch(copyTemplates(Array.from(selectedTemplateIds), request, filter))
    setSelectedTemplateIds(new Set([]))
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      p={2}
    >
      <Stack direction="row" spacing={2}>
        <TemplatesFilterDropDown
          places={places}
          placeNodes={placeNodes}
          getFilterTemplates={getFilterTemplates}
          flattenNodePath={flattenedNodePath}
          request={request}
        />
        <TemplatesSortDropDown
          getSortedTemplates={getSortedTemplates}
          templatesSortOrder={templatesSortOrder}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        {canUseTemplateQrCode === 'yes' && (
          <Button
            variant="outlined"
            disabled={!selectedTemplateIds.size}
            loading={isDownloadingQrCodes}
            loadingPosition="start"
            onClick={onClickQrCodeDownloadButton}
            startIcon={<QrCode />}
          >
            QRコード一括ダウンロード
          </Button>
        )}
        <Button
          variant="outlined"
          disabled={!selectedTemplateIds.size}
          onClick={onClickCopyButton}
          startIcon={<ContentCopy />}
        >
          コピー
        </Button>
        <Button
          variant="contained"
          onClick={createTemplatePages}
          startIcon={<AddOutlined />}
        >
          ひな形を追加
        </Button>
      </Stack>
    </Stack>
  )
}

export default Actions
